import { useCallback, useEffect, useRef, useState } from 'react';
import { stateSetter } from '@/utils';
import { IServerColumnConfig } from '@/components/table/column-config';
import {
	IProjectType,
	IProjectTypeCreator,
	IProjectTypePayload,
	ProjectTypesReturnType
} from './types';
import {
	deleteType,
	fetchTypes,
	fetchTypesTableConfig,
	fetchSingleType,
	fetchProjectTypeCreators,
	patchTypes,
	postTypes,
	fetchTypesQuery
} from './actions';

interface ILoading {
	all: boolean;
	delete: boolean;
	create: boolean;
	update: boolean;
}

// Fallback stabil ca identitate pentru `data` neîncărcat — un `[]` nou la fiecare
// randare re-declanșează în lanț efectele consumatorilor cu dependență pe `data`
// („Maximum update depth exceeded").
const EMPTY_LIST: never[] = [];

export function useProjectTypes<T extends IProjectType | IProjectType[]>(
	getTypesOnRender: boolean | undefined = undefined,
	queryParam: string | undefined = undefined,
	getAll: boolean | undefined = undefined,
	owned: boolean | undefined = undefined,
	itemsSet: number | undefined = 10,
	fspId: number | undefined = undefined,
	/**
	 * Paginarea INIȚIALĂ (de regulă cea din URL, via useListQueryState). Fără ea,
	 * starea internă pornește pe 0/10 și primul fetch pleacă cu valori greșite, urmat
	 * de un al doilea după sincronizarea din efectele-punte — două call-uri și un race
	 * pe răspunsuri. Doar valoarea de la primul render contează.
	 */
	initialPagination: { page: number; items: number } | undefined = undefined,
	withTableConfig = false
): ProjectTypesReturnType<T> {
	const [data, setData] = useState<T | null>(null);
	const [loading, setLoading] = useState<ILoading>({
		all: true,
		delete: false,
		create: false,
		update: false
	});
	// Configurația de coloane (api/project-types/table-config) — cerută doar de pagina
	// de listă (withTableConfig); la eroare rămâne null și tabelul cade silențios pe
	// coloanele definite local.
	const [tableConfig, setTableConfig] = useState<IServerColumnConfig[] | null>(null);
	const [loadingTableConfig, setLoadingTableConfig] = useState<boolean>(
		Boolean(withTableConfig)
	);
	const [page, setPage] = useState<number>(initialPagination?.page ?? 0);
	const [items, setItems] = useState<number>(initialPagination?.items ?? itemsSet);
	const [totalPages, setTotalPages] = useState<number>(1);
	const [totalItems, setTotalItems] = useState<number>(1);
	const [enableQuery, setEnableQuery] = useState<boolean>(false);
	const [creators, setCreators] = useState<IProjectTypeCreator[]>([]);

	const handleResponse = (pageParam: number, itemsParam: number): void => {
		setTotalPages(pageParam);
		setTotalItems(itemsParam);
	};

	const toggleLoading = (
		type: 'all' | 'delete' | 'create' | 'update',
		value: boolean
	): void => {
		setLoading((prevState) => ({
			...prevState,
			[type]: value
		}));
	};

	useEffect(() => {
		if (!withTableConfig) return undefined;
		let active = true;
		setLoadingTableConfig(true);
		fetchTypesTableConfig()
			.then((columns) => {
				if (active) setTableConfig(columns.length ? columns : null);
			})
			.catch(() => {
				// Fallback silențios: fără config BE, tabelul folosește coloanele locale.
				if (active) setTableConfig(null);
			})
			.finally(() => {
				if (active) setLoadingTableConfig(false);
			});
		return () => {
			active = false;
		};
	}, [withTableConfig]);

	// Gardă anti-race pe listă: la schimbări rapide de query (căutare BE cu debounce)
	// pot fi în zbor mai multe cereri, iar răspunsurile pot sosi în altă ordine —
	// se aplică DOAR ultima cerută.
	const listRequestSeq = useRef(0);

	const getProjectTypes = useCallback(
		async (pageParam: number, itemsParam: number) => {
			listRequestSeq.current += 1;
			const seq = listRequestSeq.current;
			const isCurrent = (): boolean => listRequestSeq.current === seq;
			try {
				toggleLoading('all', true);
				const response = await fetchTypes(
					pageParam,
					itemsParam,
					(tp, tc) => {
						if (isCurrent()) handleResponse(tp, tc);
					},
					getAll,
					fspId,
					owned
				);
				if (isCurrent()) setData(response as T);
			} catch {
				// eroarea ajunge în toast + jurnal prin pipeline-ul global de erori
			} finally {
				if (isCurrent()) toggleLoading('all', false);
			}
		},

		[setData]
	);

	const getProjectTypesQuery = useCallback(
		async (queryParams: string, pageParam: number, itemsParam: number) => {
			listRequestSeq.current += 1;
			const seq = listRequestSeq.current;
			const isCurrent = (): boolean => listRequestSeq.current === seq;
			try {
				!enableQuery && setEnableQuery(true);
				toggleLoading('all', true);
				const response = await fetchTypesQuery(
					queryParams,
					pageParam,
					itemsParam,
					(tp, tc) => {
						if (isCurrent()) handleResponse(tp, tc);
					},
					getAll,
					fspId,
					owned
				);
				if (isCurrent()) setData(response as T);
			} catch {
				// eroarea ajunge în toast + jurnal prin pipeline-ul global de erori
			} finally {
				if (isCurrent()) toggleLoading('all', false);
			}
		},

		[setData]
	);

	const removeProjectTypes = useCallback(
		async (typeId: number, onSuccess?: () => void) => {
			try {
				toggleLoading('delete', true);
				const response = await deleteType(typeId);
				// Acceptăm orice status 2xx (200/204) drept succes.
				if (typeof response === 'number' && response >= 200 && response < 300) {
					setData((currentData) => {
						if (Array.isArray(currentData)) {
							return currentData.filter((FSP) => FSP.id !== typeId) as T;
						}
						return currentData;
					});
					onSuccess && onSuccess();
				}
			} catch {
				// eroarea ajunge în toast + jurnal prin pipeline-ul global de erori
			} finally {
				toggleLoading('delete', false);
			}
		},

		[setData]
	);

	const createProjectTypes = useCallback(
		async (
			typeObj: IProjectTypePayload,
			onSuccess?: () => void,
			onSuccessResponse?: (res: IProjectType) => void
		) => {
			try {
				toggleLoading('create', true);
				const response = await postTypes(typeObj, onSuccessResponse);
				if (response === 201) {
					onSuccess && onSuccess();
				}
			} catch {
				// eroarea ajunge în toast + jurnal prin pipeline-ul global de erori
			} finally {
				toggleLoading('create', false);
			}
		},

		[setData]
	);

	const editProjectTypes = useCallback(
		async (
			typeId: number,
			updatedObject: IProjectTypePayload,
			onSuccess?: () => void
		) => {
			try {
				toggleLoading('update', true);
				const response = await patchTypes(typeId, updatedObject);
				if (response === 200) {
					onSuccess && onSuccess();
				}
			} catch {
				// eroarea ajunge în toast + jurnal prin pipeline-ul global de erori
			} finally {
				toggleLoading('update', false);
			}
		},

		[setData]
	);

	useEffect(() => {
		if (getTypesOnRender && queryParam === undefined) {
			getProjectTypes(page, items);
		}

	}, [getTypesOnRender, page, items]);

	useEffect(() => {
		if (queryParam) {
			getProjectTypesQuery(queryParam, page, items);
		} else if (enableQuery) {
			getProjectTypes(page, items);
		}

	}, [queryParam, page, items]);

	const getProjectType = useCallback(async (typeId: number) => {
		try {
			return await fetchSingleType(typeId);
		} catch {
			return null;
		}
	}, []);

	const getCreators = useCallback(async () => {
		try {
			const response = await fetchProjectTypeCreators();
			setCreators(response);
		} catch {
			// eroarea ajunge în toast + jurnal prin pipeline-ul global de erori
		}
	}, []);

	useEffect(() => {
		if (getTypesOnRender) {
			getCreators();
		}

	}, [getTypesOnRender, getCreators]);

	return {
		data: data || (EMPTY_LIST as IProjectType[] as T),
		setData: setData as stateSetter<T>,
		loading: loading.all,
		tableConfig,
		loadingTableConfig,
		loadingDelete: loading.delete,
		loadingCreate: loading.create,
		loadingUpdate: loading.update,
		creators,
		getCreators,
		createProjectTypes,
		editProjectTypes,
		getProjectTypes,
		getProjectType,
		getProjectTypesQuery,
		removeProjectTypes,
		items,
		page,
		setItems,
		setPage,
		totalItems,
		totalPages
	};
}
